import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useWebHaptics } from "web-haptics/react";
import { useSession, signOut } from "@/lib/auth-client";
import { useOrg } from "@/hooks/useOrg";
import {
  LayoutDashboard,
  Webhook,
  Send,
  GitBranch,
  Activity,
  AppWindow,
  Database,
  AlertCircle,
  BarChart3,
  Settings,
  PanelLeftClose,
  PanelLeft,
  Megaphone,
  RotateCcw,
  Tag,
  LogOut,
  ChevronDown,
  Plus,
  Check,
  Layers,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/api";

const navSections = [
  {
    label: "Receive Webhooks",
    items: [
      { path: "/dashboard", label: "Overview", icon: LayoutDashboard },
      { path: "/sources", label: "Sources", icon: Webhook },
      { path: "/events", label: "Events", icon: Activity },
    ],
  },
  {
    label: "Deliver",
    items: [
      { path: "/destinations", label: "Destinations", icon: Send },
      { path: "/connections", label: "Connections", icon: GitBranch },
      { path: "/topics", label: "Topics", icon: Megaphone },
      { path: "/replay", label: "Replay", icon: RotateCcw },
    ],
  },
  {
    label: "Outbound",
    items: [
      { path: "/applications", label: "Applications", icon: AppWindow },
      { path: "/event-types", label: "Event Types", icon: Tag },
    ],
  },
  {
    label: "Data",
    items: [
      { path: "/databases", label: "Databases", icon: Database },
    ],
  },
  {
    label: "Monitor",
    items: [
      { path: "/issues", label: "Issues", icon: AlertCircle },
      { path: "/metrics", label: "Metrics", icon: BarChart3 },
    ],
  },
];

const bottomItems = [
  { path: "/settings", label: "Settings", icon: Settings },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const haptic = useWebHaptics();
  const { envId, envName, environments, switchEnv, refetchEnvironments } = useOrg();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("hookstream-sidebar") === "collapsed";
  });
  const [createEnvOpen, setCreateEnvOpen] = useState(false);
  const [newEnvName, setNewEnvName] = useState("");
  const [newEnvSlug, setNewEnvSlug] = useState("");
  const [createEnvLoading, setCreateEnvLoading] = useState(false);
  const [createEnvError, setCreateEnvError] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(
      "hookstream-sidebar",
      collapsed ? "collapsed" : "expanded"
    );
  }, [collapsed]);

  const isActive = (path: string) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  const userInitial = session?.user?.name?.[0]?.toUpperCase() || session?.user?.email?.[0]?.toUpperCase() || "?";

  async function handleSignOut() {
    await signOut();
    navigate("/sign-in");
  }

  async function handleCreateEnv() {
    if (!newEnvName.trim() || !newEnvSlug.trim()) return;
    setCreateEnvLoading(true);
    setCreateEnvError(null);
    try {
      await api("/environments", {
        method: "POST",
        body: { name: newEnvName.trim(), slug: newEnvSlug.trim() },
      });
      haptic.trigger("success");
      setCreateEnvOpen(false);
      setNewEnvName("");
      setNewEnvSlug("");
      await refetchEnvironments();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create environment";
      setCreateEnvError(msg);
      haptic.trigger("error");
    } finally {
      setCreateEnvLoading(false);
    }
  }

  function handleNameChange(value: string) {
    setNewEnvName(value);
    // Auto-generate slug from name
    setNewEnvSlug(value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  }

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          "flex h-screen flex-col border-r border-sidebar-border bg-[#1e272c] transition-all duration-200",
          collapsed ? "w-16" : "w-60"
        )}
      >
        {/* Logo */}
        <div
          className={cn(
            "flex h-14 items-center border-b border-sidebar-border px-4",
            collapsed ? "justify-center" : "gap-2"
          )}
        >
          <Link to="/" className="flex items-center">
            {collapsed ? (
              <img src="/favicon.svg" alt="hookstream" className="h-6 w-6" />
            ) : (
              <img src="/logo.svg" alt="hookstream" className="h-5" />
            )}
          </Link>
        </div>

        {/* Environment switcher */}
        {environments.length > 0 && (
          <div className={cn("border-b border-sidebar-border", collapsed ? "px-2 py-2" : "px-3 py-2")}>
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={cn(
                        "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs transition-colors hover:bg-[rgba(206,214,255,0.05)]",
                        collapsed ? "justify-center" : "justify-between"
                      )}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Layers className="h-3.5 w-3.5 shrink-0 text-[#3cd2a5]" />
                        {!collapsed && (
                          <span className="truncate text-[rgba(206,214,255,0.7)] uppercase tracking-wider font-medium">
                            {envName}
                          </span>
                        )}
                      </span>
                      {!collapsed && <ChevronDown className="h-3 w-3 shrink-0 text-[rgba(206,214,255,0.3)]" />}
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right">{envName}</TooltipContent>}
              </Tooltip>
              <DropdownMenuContent align="start" side="bottom" sideOffset={4} className="w-52">
                <DropdownMenuLabel>Projects</DropdownMenuLabel>
                {environments.map((env) => (
                  <DropdownMenuItem
                    key={env.id}
                    onClick={() => {
                      if (env.id !== envId) {
                        haptic.trigger("selection");
                        switchEnv(env.id);
                      }
                    }}
                    className="flex items-center justify-between"
                  >
                    <span className="truncate">{env.name}</span>
                    {env.id === envId && <Check className="h-3.5 w-3.5 shrink-0 text-[#3cd2a5]" />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    haptic.trigger("selection");
                    setCreateEnvOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5 mr-2" />
                  New Project
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Main nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {navSections.map((section, sIdx) => (
            <div key={section.label} className={sIdx > 0 ? "mt-4" : ""}>
              {!collapsed && (
                <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[rgba(206,214,255,0.3)] font-semibold">
                  {section.label}
                </div>
              )}
              {collapsed && sIdx > 0 && (
                <Separator className="my-2 mx-2" />
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);

                  const linkContent = (
                    <Link
                      to={item.path}
                      onClick={() => haptic.trigger("selection")}
                      className={cn(
                        "flex items-center gap-2.5 py-1.5 px-2.5 text-xs uppercase tracking-wider font-medium transition-[color,background-color,border-color] duration-200 ease-out motion-reduce:transition-none",
                        active
                          ? "border-l-2 border-[#3cd2a5] bg-[rgba(206,214,255,0.08)] text-white"
                          : "border-l-2 border-transparent text-[rgba(206,214,255,0.5)] hover:text-[rgba(206,214,255,0.75)] hover:bg-[rgba(206,214,255,0.05)]",
                        collapsed && "justify-center px-2"
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {!collapsed && <span>{item.label}</span>}
                    </Link>
                  );

                  if (collapsed) {
                    return (
                      <Tooltip key={item.path}>
                        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                        <TooltipContent side="right">{item.label}</TooltipContent>
                      </Tooltip>
                    );
                  }

                  return <div key={item.path}>{linkContent}</div>;
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="p-2">
          <Separator className="mb-2" />

          {bottomItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            const linkContent = (
              <Link
                to={item.path}
                onClick={() => haptic.trigger("selection")}
                className={cn(
                  "flex items-center gap-2.5 py-1.5 px-2.5 text-xs uppercase tracking-wider font-medium transition-[color,background-color,border-color] duration-200 ease-out motion-reduce:transition-none",
                  active
                    ? "border-l-2 border-[#3cd2a5] bg-[rgba(206,214,255,0.08)] text-white"
                    : "border-l-2 border-transparent text-[rgba(206,214,255,0.5)] hover:text-[rgba(206,214,255,0.75)] hover:bg-[rgba(206,214,255,0.05)]",
                  collapsed && "justify-center px-2"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );

            if (collapsed) {
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              );
            }

            return <div key={item.path}>{linkContent}</div>;
          })}

          {/* Collapse toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size={collapsed ? "icon" : "default"}
                onClick={() => setCollapsed(!collapsed)}
                className={cn(
                  "w-full text-[rgba(206,214,255,0.5)] hover:bg-[rgba(206,214,255,0.05)]",
                  collapsed ? "justify-center" : "justify-start gap-3 px-3"
                )}
              >
                {collapsed ? (
                  <PanelLeft className="h-4 w-4 shrink-0" />
                ) : (
                  <PanelLeftClose className="h-4 w-4 shrink-0" />
                )}
                {!collapsed && <span className="text-xs uppercase tracking-wider">Collapse</span>}
              </Button>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent side="right">Expand sidebar</TooltipContent>
            )}
          </Tooltip>

          {/* User section */}
          <div
            className={cn(
              "mt-2 flex items-center px-3 py-2",
              collapsed ? "justify-center px-2" : "gap-3"
            )}
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
              {userInitial}
            </div>
            {!collapsed && (
              <div className="flex flex-1 items-center justify-between">
                <span className="truncate text-xs text-[rgba(206,214,255,0.5)]">
                  {session?.user?.name || session?.user?.email || "Account"}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleSignOut}
                      className="text-[rgba(206,214,255,0.3)] hover:text-[rgba(206,214,255,0.6)] transition-colors"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Sign out</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Create Project Dialog */}
      <Dialog open={createEnvOpen} onOpenChange={(open) => { setCreateEnvOpen(open); if (!open) { setNewEnvName(""); setNewEnvSlug(""); setCreateEnvError(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Project</DialogTitle>
            <DialogDescription>
              Create a new project to isolate your sources, destinations, and connections.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="env-name">Name</Label>
              <Input
                id="env-name"
                placeholder="e.g. Staging"
                value={newEnvName}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="env-slug">Slug</Label>
              <Input
                id="env-slug"
                placeholder="e.g. staging"
                value={newEnvSlug}
                onChange={(e) => setNewEnvSlug(e.target.value)}
              />
              <p className="text-[10px] text-muted-foreground">
                Lowercase, alphanumeric, and hyphens only.
              </p>
            </div>
            {createEnvError && (
              <p className="text-xs text-red-400">{createEnvError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateEnvOpen(false)} disabled={createEnvLoading}>
              Cancel
            </Button>
            <Button onClick={handleCreateEnv} disabled={createEnvLoading || !newEnvName.trim() || !newEnvSlug.trim()}>
              {createEnvLoading ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
